import {
  type EmployeeGoal,
  type EmployeeProfile,
} from '../api/employee.service';
import {
  DEFAULT_COUNTRY_CODE,
  resolvePhoneCountryCode,
  type CountryCode,
} from '../config';

import { GOAL_AMOUNT_MIN } from './goal-amount';
import { normalizeNamePart } from './personal-data';

export type EmployeeIdentity = {
  goal: EmployeeGoal | null;
  profile: EmployeeProfile | null;
};

type EmployeeAuthContactLike = {
  countryCode: CountryCode;
} | null;

export function resolveEmployeeCountryCode(
  profile: EmployeeProfile | null,
): CountryCode {
  const profileCountryCode = profile?.countryCode;

  return resolvePhoneCountryCode(profileCountryCode);
}

export function resolveEmployeeSessionCountryCode(
  profile: EmployeeProfile | null,
  authContact: EmployeeAuthContactLike,
): CountryCode {
  if (!profile) {
    return authContact?.countryCode ?? DEFAULT_COUNTRY_CODE;
  }

  if (
    typeof profile.countryCode !== 'string' ||
    profile.countryCode.length === 0
  ) {
    return authContact?.countryCode ?? DEFAULT_COUNTRY_CODE;
  }

  return resolveEmployeeCountryCode(profile);
}

export function resolveEmployeeFullName(
  profile: EmployeeProfile | null,
): string | null {
  const firstName = normalizeNamePart(profile?.firstName ?? '');
  const lastName = normalizeNamePart(profile?.lastName ?? '');

  if (firstName.length === 0 || lastName.length === 0) {
    return null;
  }

  return `${firstName} ${lastName}`;
}

export function resolveEmployeeFirstName(
  profile: EmployeeProfile | null,
): string | null {
  const firstName = normalizeNamePart(profile?.firstName ?? '');

  return firstName.length > 0 ? firstName : null;
}

export function resolveEmployeeProfileName(
  profile: EmployeeProfile | null,
  fallbackProfileName: string,
): string {
  return resolveEmployeeFullName(profile) ?? fallbackProfileName;
}

export function resolveEmployeeShowRating(
  profile: EmployeeProfile | null,
  defaultValue = true,
): boolean {
  return profile?.showRating ?? defaultValue;
}

export function resolveEmployeeGoalText(
  goal: EmployeeGoal | null,
  fallbackGoalText = '',
): string {
  const goalText = goal?.text.trim() ?? '';

  return goalText.length > 0 ? goalText : fallbackGoalText;
}

export function resolveEmployeeGoalTargetAmount(
  goal: EmployeeGoal | null,
): number | null {
  const targetAmount = goal?.targetAmount;

  return typeof targetAmount === 'number' && targetAmount >= GOAL_AMOUNT_MIN
    ? targetAmount
    : null;
}
